'use strict';

import defaultConfig from '../../settings/config';
import { Component } from '../../viewEngin/pview';
import Point from '../../core/point';
import UtilCore from '../../core/util.core';
import UiCore from '../../core/ui.core';
import GeomCore from '../.././core/geom.core';
import ConnectorBox from '../connectorBox/connectorBox.component';
import RichTextBox from '../richTextBox/richTextBox.component';
import SpeechBox from '../speechBox/speechBox.components';
import { IAnnotationLabelsProps } from './annotationLabels.model';
import { AXIS_TYPE, FLOAT, HORIZONTAL_ALIGN } from '../../global/global.enums';
import { IVnode } from '../../viewEngin/component.model';
import { IAnnotationConfig, IAnnotationLabel, IAnnotationOptions } from '../../charts/connectedPointChartsType/connectedPointChartsType.model';
import Store from '../../liveStore/store';
import storeManager from '../../liveStore/storeManager';


/**
 * annotationLabels.component.tsx
 * @createdOn:28-Aug-2021
 * @author:SmartChartsNXT
 * @description: This components will create annotation labels at the various point of interest.
 * @extends: Component
 */

class AnnotationLabels extends Component<IAnnotationLabelsProps> {
  private rid: string;
  private clipPathId: string;
  private storeData: Store;

  constructor(props: IAnnotationLabelsProps) {
    super(props);
    this.rid = UtilCore.getRandomID();
    this.storeData = storeManager.getStore((this as any).context.runId);
    this.clipPathId = 'sc-clip-' + this.rid;
    this.state = {
      annotations: [],
      mouseDown: false,
      mouseDownPos: null,
      grpIndex: null,
      labelIndex: null
    };
    this.createAnnotationState(this.props);
    this.minimizeLabel = this.minimizeLabel.bind(this);
    this.maximizeLabel = this.maximizeLabel.bind(this);
    this.labelMouseDown = this.labelMouseDown.bind(this);
    this.labelMouseMove = this.labelMouseMove.bind(this);
    this.labelMouseUp = this.labelMouseUp.bind(this);
  }

  createAnnotationState(props: IAnnotationLabelsProps) {
    let annotations = props.annotations;
    this.state.annotations = [];
    let defaultOpt: IAnnotationOptions = {
      isDraggable: true,
      isCollapsible: true, //After collapse the annotation label become small plus icon
      offsetX: 0, // Move entire label along with anchor point to left or right
      offsetY: -10, // Move entire label along with anchor point to top or bottom
      xPadding: 5, // Create x padding inside label box
      yPadding: 5, // Create y padding inside label box
      float: FLOAT.TOP,
      textColor: defaultConfig.theme.fontColorDark,
      bgColor: defaultConfig.theme.bgColorLight,
      fontSize: 12,
      fontFamily: defaultConfig.theme.fontFamily,
      borderColor: defaultConfig.theme.bgColorDark,
      borderWidth: 1,
      borderRadius: 5,
      opacity: 0.5, // Label background opacity
      dropShadow: true,
      maxWidth: 150,
      minHeight: 30,
      rotate: 0
    };
    for (let annotation of annotations) {
      for (let label of annotation.labels) {
        if (label.isMinimized === undefined) {
          label.isMinimized = false;
        }
        if (!label.topOffset) {
          label.topOffset = 0;
          label.nextTopOffset = 0;
        }
        if (!label.leftOffset) {
          label.leftOffset = 0;
          label.nextLeftOffset = 0;
        }
      }
      if (annotation.options.isDraggable && annotation.options.bgColor === 'none') {
        annotation.options.opacity = 0.0001;
        annotation.options.bgColor = '#FFF';
      }
      this.state.annotations.push({
        options: { ...defaultOpt, ...annotation.options },
        labels: [...annotation.labels]
      });
    }
  }

  render(): IVnode {
    return (
      <g class='sc-annotation-labels-container' transform={`translate(${this.props.posX},${this.props.posY})`} clip-path={`url(#${this.clipPathId})`}>
        {this.getStyle()}
        <defs>
          <radialGradient id="mIconGradInactive">
            <stop offset="10%" stop-color="#fff" />
            <stop offset="95%" stop-color="#555" />
          </radialGradient>
        </defs>
        <defs>
          <clipPath id={this.clipPathId}>
            <rect x={0} y={0} width={this.props.width} height={this.props.height} />
          </clipPath>
        </defs>
        {this.drawAnnotationGroup()}
        <rect class="sc-annotation-drag-plane" x={0} y={0} width={this.props.width} height={this.props.height} stroke='none' fill={this.state.mouseDown ? '#fff' : 'none'} fill-opacity={0}
          events={{
            mousemove: (e: MouseEvent) => this.labelMouseMove(e),
            mouseup: (e: MouseEvent) => this.labelMouseUp(e)
          }} >
        </rect>
      </g>
    );
  }

  drawAnnotationGroup(): IVnode {
    return this.state.annotations.map((annotation: IAnnotationConfig, index: number) => {
      return (
        <g class={`sc-annotation-label-group-${index}`} transform={`translate(${this.props.vTransformX}, 0)`}>
          {this.drawAnnotationLabels(annotation, index)}
        </g>
      );
    });
  }

  getXPositionValueFromIndex(indexValue: number) {
    let allCategories = this.props.allCategorySet;
    const xPositionWithDynamicScaleFn = this.storeData.getValue('xPositionWithDynamicScaleFn');
    let xPosCategoryValue = 0;
    let lowerCategoryValue = allCategories[Math.floor(indexValue) - 1] as number;
    let fractionalPart = (indexValue - Math.floor(indexValue));
    if (fractionalPart > 0) {
      let upperCategoryValue = allCategories[Math.ceil(indexValue) - 1] as number;
      let valueDiff = upperCategoryValue - lowerCategoryValue;
      xPosCategoryValue = lowerCategoryValue + (valueDiff * fractionalPart);
    } else {
      xPosCategoryValue = lowerCategoryValue;
    }
    return xPositionWithDynamicScaleFn(indexValue, xPosCategoryValue);
  }

  drawAnnotationLabels(annotation: IAnnotationConfig, grpIndex: number): IVnode[] {
    let config = annotation.options;
    let scaleY = this.storeData.getValue('scaleY');
    let baseLine = this.storeData.getValue('baseLine');
    return annotation.labels.map((label: IAnnotationLabel, index: number) => {
      let valueY = label.y;
      if (this.props.yAxisType === AXIS_TYPE.LOGARITHMIC && valueY > 0) {
        valueY = Math.log10(valueY);
      }
      let dataPoint: Point;
      let posX = 0;
      if (this.storeData.getValue('parseAsNumber')) {
        posX = this.getXPositionValueFromIndex(label.x) - (label.leftOffset + label.nextLeftOffset);
      } else {
        const scaleX = this.storeData.getValue('scaleX')
        posX = ((label.x - this.props.leftIndex) * scaleX) - (label.leftOffset + label.nextLeftOffset);
      }
      let posY = baseLine - (valueY * scaleY) - (label.topOffset + label.nextTopOffset)
      dataPoint = new Point(posX, posY);
      const isDataVisible = this.getDataPointVisibility(new Point(dataPoint.x + label.leftOffset + label.nextLeftOffset, dataPoint.y));
      if (label.text) {
        return this.drawAnnotationTypeText(grpIndex, index, label, config, dataPoint, isDataVisible);
      } else if (label.shape) {
        return this.drawAnnotationTypeShape(grpIndex, index, label, config, dataPoint);
      } else if (label.image) {
        return this.drawAnnotationTypeImage(grpIndex, index, label, config, dataPoint);
      }
    });
  }

  drawAnnotationTypeShape(grpIndex: number, index: number, label: IAnnotationLabel, config: IAnnotationOptions, dataPoint: Point): IVnode {
    return (
      <g class={`sc-annotation-label-${grpIndex}-${index}`} transform={`rotate(${config.rotate}, ${dataPoint.x}, ${dataPoint.y}) translate(${dataPoint.x}, ${dataPoint.y})`}>
        {config.dropShadow && UiCore.dropShadow('sc-annotation-shape-drop-shadow')}
        <path class='sc-annotation-label-shape' d={label.shape} fill={config.bgColor} stroke={config.borderColor} opacity={config.opacity} stroke-width={config.borderWidth}
          filter={config.dropShadow ? 'url(#sc-annotation-shape-drop-shadow)' : ''} shape-rendering='geometricPrecision' vector-effect='non-scaling-stroke'>
        </path>
      </g>
    );
  }

  drawAnnotationTypeImage(grpIndex: number, index: number, label: IAnnotationLabel, config: IAnnotationOptions, dataPoint: Point): IVnode {
    return (
      <g class={`sc-annotation-label-${grpIndex}-${index}`} transform={`rotate(${config.rotate}, ${dataPoint.x}, ${dataPoint.y}) translate(${dataPoint.x}, ${dataPoint.y})`}>
        {config.dropShadow && UiCore.dropShadow('sc-annotation-image-drop-shadow')}
        <image class='sc-annotation-label-img' width={label.width || 50} height={label.height || 50} href={label.image} opacity={config.opacity}
          filter={config.dropShadow ? 'url(#sc-annotation-shape-drop-shadow)' : ''}>
        </image>
      </g>
    );
  }

  drawAnnotationTypeText(grpIndex: number, index: number, label: IAnnotationLabel, config: IAnnotationOptions, dataPoint: Point, isDataVisible: boolean): IVnode {
    let boxWidth: number = label.contentWidth ? (label.contentWidth + (2 * config.xPadding)) : config.maxWidth;
    let boxHeight: number = label.contentHeight ? (label.contentHeight + (2 * config.yPadding) + 5) : config.minHeight;
    const hasRotation: boolean = config.rotate !== 0;
    const hasSpeechBubble: boolean = !(label.speechBubble === false) && !hasRotation;

    const noFloat: boolean = !hasSpeechBubble;
    if (label.isMinimized === true) {
      boxWidth = 30;
      boxHeight = 30;
    }
    const boxCenter: Point = new Point(boxWidth / 2, boxHeight / 2);
    const labelBoxPos: Point = this.getLabelPosition(dataPoint, boxWidth, boxHeight, config, noFloat);
    const anchorPoint: Point = new Point(dataPoint.x - labelBoxPos.x + label.leftOffset + label.nextLeftOffset + config.offsetX, dataPoint.y - labelBoxPos.y + label.topOffset + label.nextTopOffset + config.offsetY);
    const anchorDistance: number = GeomCore.getDistanceBetween(boxCenter, anchorPoint);
    let labelBox: IVnode;
    if (noFloat) {
      labelBox = <g class='sc-annotation-label-box-bg'>
        {config.dropShadow && UiCore.dropShadow('sc-annotation-bg-drop-shadow')}
        <rect x='0' y='0' width={boxWidth} height={boxHeight} filter={config.dropShadow ? 'url(#sc-annotation-bg-drop-shadow)' : ''}
          fill-opacity={config.opacity} fill={config.bgColor} stroke-width={config.borderWidth} stroke={config.borderColor} rx={config.borderRadius}
          shape-rendering='optimizeSpeed' vector-effect='non-scaling-stroke' ></rect>
      </g>;
    } else {
      labelBox = anchorDistance > 100 ?
        <ConnectorBox x={0} y={0} width={boxWidth} height={boxHeight} cpoint={new Point(anchorPoint.x, anchorPoint.y)}
          bgColor={config.bgColor} fillOpacity={config.opacity} shadow={config.dropShadow} strokeColor={config.borderColor} strokeWidth={config.borderWidth}
          cornerRadius={config.borderRadius} showConnectorLine={!hasRotation}>
        </ConnectorBox>
        :
        <SpeechBox x={0} y={0} width={boxWidth} height={boxHeight} cpoint={new Point(anchorPoint.x, anchorPoint.y)}
          bgColor={config.bgColor} fillOpacity={config.opacity} shadow={config.dropShadow} strokeColor={config.borderColor} strokeWidth={config.borderWidth}
          anchorBaseWidth={7} cornerRadius={config.borderRadius} showAnchor={!hasRotation}>
        </SpeechBox>;
    }

    if (!isDataVisible) {
      return (
        <g class={`sc-annotation-label-${grpIndex}-${index}`}></g>
      );
    }
    if (label.isMinimized === true && config.isCollapsible) {
      return (
        <g class={`sc-annotation-label-${grpIndex}-${index}`} transform={`translate(${labelBoxPos.x},${labelBoxPos.y})`}>
          <title>Maximize</title>
          <g>
            <rect class='sc-maximize-icon-bg' x={0} y={0} width={boxWidth} height={boxWidth} rx="5" fill="url('#mIconGradInactive')" stroke="none" fill-opacity={0.5}
              events={{
                click: (e: MouseEvent) => this.maximizeLabel(e, grpIndex, index)
              }}>
            </rect>
          </g>
          <text text-rendering='geometricPrecision' stroke='#000' fill='#000' font-size='22' pointer-events='none'>
            <tspan text-anchor='middle' x={boxWidth / 2} y={boxWidth / 2} dominant-baseline="middle">+</tspan>
          </text>
        </g>
      );
    }
    return (
      <g class={`sc-annotation-label-${grpIndex}-${index}`} transform={`rotate(${config.rotate}, ${labelBoxPos.x}, ${labelBoxPos.y}) translate(${labelBoxPos.x},${labelBoxPos.y})`} tabindex={0}>
        <g class='sc-annotation-label-box' style={{ 'cursor': config.isDraggable ? 'move' : 'initial' }}
          events={{ mousedown: (e: MouseEvent) => this.labelMouseDown(e, grpIndex, index) }}>
          {labelBox}
        </g>
        <RichTextBox class='sc-annotation-label-text' posX={config.xPadding} posY={config.yPadding} contentWidth={config.maxWidth - (2 * config.xPadding)}
          textAlign={HORIZONTAL_ALIGN.CENTER} verticalAlignMiddle={true} fontSize={config.fontSize} textColor={config.textColor} text={label.text || ''}
          onRef={(ref: RichTextBox) => {
            if (ref) {
              let textDim: { width: number, height: number } = ref.getContentDim();
              label.contentWidth = textDim.width;
              label.contentHeight = textDim.height;
            }
          }}>
        </RichTextBox>
        {config.isCollapsible && this.getMinimizeIcon(boxWidth, config.borderRadius, grpIndex, index)}
      </g>
    );
  }

  getMinimizeIcon(boxWidth: number, radius: number, grpIndex: number, labelIndex: number): IVnode {
    let iconWidth: number = 20;
    let iconHeight: number = 20;
    let iconPath: (string | number)[] = ['M', 0, 0, 'L', iconWidth - radius, 0, 'a', radius, radius, ' 0 0 1 ', radius, radius, 'L', iconWidth, iconHeight, 'Z'];
    return (
      <g class="sc-minimize-icon-group" transform={`translate(${boxWidth - iconWidth}, ${0})`}>
        <title>Minimize</title>
        <g>
          <path class='sc-minimize-icon-bg' d={iconPath.join(' ')} fill="#000" fill-opacity={0.5}
            events={{
              click: (e: MouseEvent) => this.minimizeLabel(e, grpIndex, labelIndex)
            }}>
          </path>
        </g>
      </g>
    );
  }

  getMaximizeIcon(boxWidth: number, grpIndex: number, labelIndex: number): IVnode {
    return (
      <g class="sc-maximize-icon-group">
        <title>Maximize</title>
        <g>
          <rect class='sc-maximize-icon-bg' x={0} y={0} width={boxWidth} height={boxWidth} rx="5" fill="url('#mIconGradInactive')" stroke="none" fill-opacity={0.5}
            events={{
              click: (e: MouseEvent) => this.maximizeLabel(e, grpIndex, labelIndex)
            }}>
          </rect>
        </g>
        <text text-rendering='geometricPrecision' stroke='#000' fill='#000' font-size='22' pointer-events='none'>
          <tspan text-anchor='middle' x={boxWidth / 2} y={boxWidth / 2} dominant-baseline="middle">+</tspan>
        </text>
      </g>
    );
  }

  minimizeLabel(e: MouseEvent, grpIndex: number, labelIndex: number): void {
    this.state.annotations[grpIndex].labels[labelIndex].isMinimized = true;
    this.update();
  }

  maximizeLabel(e: MouseEvent, grpIndex: number, labelIndex: number): void {
    this.state.annotations[grpIndex].labels[labelIndex].isMinimized = false;
    this.update();
  }

  getLabelPosition(dataPoint: Point, boxWidth: number, boxHeight: number, config: IAnnotationOptions, noFloat: boolean, recursion: number = 1): Point {
    let labelBoxPos: Point;
    let anchorHeight: number = 7;
    if (noFloat) {
      labelBoxPos = new Point(dataPoint.x, dataPoint.y);
    } else {
      switch (config.float) {
        case FLOAT.TOP: {
          labelBoxPos = new Point(dataPoint.x - (boxWidth / 2), dataPoint.y - (boxHeight + anchorHeight));
          break;
        }
        case FLOAT.BOTTOM: {
          labelBoxPos = new Point(dataPoint.x - (boxWidth / 2), dataPoint.y + (anchorHeight));
          break;
        }
        case FLOAT.LEFT: {
          labelBoxPos = new Point(dataPoint.x - (boxWidth + anchorHeight), dataPoint.y - (boxHeight / 2));
          break;
        }
        case FLOAT.RIGHT: {
          labelBoxPos = new Point(dataPoint.x + anchorHeight, dataPoint.y - (boxHeight / 2));
          break;
        }
      }
      labelBoxPos.x += config.offsetX;
      labelBoxPos.y += config.offsetY;

      if (recursion <= 4) {
        if (labelBoxPos.y < 0) {
          let cnf = { ...config, ...{ float: FLOAT.BOTTOM } };
          return this.getLabelPosition(dataPoint, boxWidth, boxHeight, cnf, noFloat, recursion + 1);
        }
        if (labelBoxPos.y + boxHeight > this.props.height) {
          let cnf = { ...config, ...{ float: FLOAT.TOP } };
          return this.getLabelPosition(dataPoint, boxWidth, boxHeight, cnf, noFloat, recursion + 1);
        }
      }

      if (labelBoxPos.x + this.props.vTransformX < 0) {
        labelBoxPos.x = -(this.props.vTransformX);
      }
      if (labelBoxPos.x + boxWidth + this.props.paddingX + this.props.vTransformX > this.props.width) {
        let diff = (labelBoxPos.x + boxWidth + this.props.paddingX + this.props.vTransformX) - this.props.width;
        labelBoxPos.x -= diff;
        if (config.float === FLOAT.BOTTOM && labelBoxPos.y < 0) {
          labelBoxPos.y = 0;
        }
      }
    }
    return labelBoxPos;
  }

  getDataPointVisibility(dataPoint: Point): boolean {
    let isVisible: boolean = true;
    if (dataPoint.x < 0 || (dataPoint.x == 0 && this.props.vTransformX < 0)) {
      isVisible = false;
    }
    if (dataPoint.x + this.props.vTransformX > this.props.width) {
      isVisible = false;
    }
    return isVisible;
  }

  labelMouseDown(e: MouseEvent, grpIndex: number, labelIndex: number): void {
    if (!this.state.annotations[grpIndex].options.isDraggable) {
      return;
    }
    let mouseDownPos: DOMPoint = UiCore.cursorPoint((this as any).context.rootContainerId, e);
    let label = this.state.annotations[grpIndex].labels[labelIndex];
    if (!label.mouseDownPos) {
      label.mouseDownPos = mouseDownPos;
    }
    label.mouseDownPos = mouseDownPos;
    this.setState({
      mouseDown: true,
      grpIndex,
      labelIndex
    });
  }

  labelMouseMove(event: MouseEvent): void {
    if (this.state.mouseDown) {
      let mousePosNow = UiCore.cursorPoint((this as any).context.rootContainerId, event);
      let movingLabel = this.state.annotations[this.state.grpIndex].labels[this.state.labelIndex];
      let nextLeftOffset = movingLabel.mouseDownPos.x - mousePosNow.x;
      let nextTopOffset = movingLabel.mouseDownPos.y - mousePosNow.y;
      movingLabel.nextLeftOffset = nextLeftOffset;
      movingLabel.nextTopOffset = nextTopOffset;
      this.update();
    }
  }

  labelMouseUp(event: MouseEvent): void {
    let movingLabel = this.state.annotations[this.state.grpIndex].labels[this.state.labelIndex];
    movingLabel.leftOffset += movingLabel.nextLeftOffset;
    movingLabel.topOffset += movingLabel.nextTopOffset;
    movingLabel.nextLeftOffset = 0;
    movingLabel.nextTopOffset = 0;
    this.setState({
      mouseDown: false,
      mouseDownPos: null,
      grpIndex: null,
      labelIndex: null
    });
  }

  getStyle(): IVnode {
    return (
      <style>
        {`
          #${(this as any).context.rootSvgId} .sc-annotation-label-text {
            pointer-events: none;
          }
          #${(this as any).context.rootSvgId} .sc-minimize-icon-bg {
            transition-duration: .15s;
            transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
            transition-property: fill-opacity;
            cursor:pointer;
          }
          #${(this as any).context.rootSvgId} .sc-minimize-icon-bg:hover, #${(this as any).context.rootSvgId} .sc-minimize-icon-bg:focus {
            fill-opacity: 1;
          }
          #${(this as any).context.rootSvgId} .sc-maximize-icon-bg {
            transition-duration: .15s;
            transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
            transition-property: fill-opacity;
            cursor:pointer;
          }
          #${(this as any).context.rootSvgId} .sc-maximize-icon-bg:hover, #${(this as any).context.rootSvgId} .sc-maximize-icon-bg:focus {
            fill-opacity: 1;
          }
        `}
      </style>
    );
  }

}



export default AnnotationLabels;